import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FileSpreadsheet, FileText, Calendar, ArrowLeft, 
  TrendingUp, DollarSign, Users, Package, Download
} from 'lucide-react';
import { reportsAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { toast } from 'react-hot-toast';
import ListPageHeader from '../../components/shared/ListPageHeader';
import ColumnFilter from '../../components/shared/ColumnFilter';

const GSTR1Page = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [gstr1Data, setGstr1Data] = useState(null);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [exporting, setExporting] = useState(false);
  const [activeSection, setActiveSection] = useState('b2b');

  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    gstin: '',
    invoice_number: '',
    invoice_value: { min: '', max: '' }
  });

  useEffect(() => {
    if (businessId) {
      fetchGSTR1();
    }
  }, [month, year, businessId]);

  const fetchGSTR1 = async () => {
    try {
      setLoading(true);
      const response = await reportsAPI.gstr1({ month, year });
      setGstr1Data(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      setExporting(true);
      const response = await reportsAPI.gstr1Export({ month, year, format });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `GSTR1_${month.toString().padStart(2, '0')}_${year}.${format === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const formatCompactCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount?.toFixed(0) || 0}`;
  };

  const { summary, b2b_invoices, b2c_small, hsn_summary } = gstr1Data || {};
  
  const months = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(2000, i).toLocaleString('default', { month: 'long' })
  }));

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const monthLabel = new Date(year, month - 1).toLocaleString('default', { month: 'short', year: 'numeric' });

  const sections = [
    { id: 'b2b', label: 'B2B Invoices', icon: Users },
    { id: 'b2c', label: 'B2C (Small)', icon: TrendingUp },
    { id: 'hsn', label: 'HSN Summary', icon: Package },
  ];

  // Apply filters to B2B invoices
  const filteredB2B = (b2b_invoices || []).filter(inv => {
    const matchesGstin = !columnFilters.gstin || 
      inv.customer_gstin?.toLowerCase().includes(columnFilters.gstin.toLowerCase());
    const matchesInvoice = !columnFilters.invoice_number || 
      inv.invoice_number?.toLowerCase().includes(columnFilters.invoice_number.toLowerCase());
    const matchesValue = (!columnFilters.invoice_value?.min || inv.invoice_value >= parseFloat(columnFilters.invoice_value.min)) &&
      (!columnFilters.invoice_value?.max || inv.invoice_value <= parseFloat(columnFilters.invoice_value.max));
    return matchesGstin && matchesInvoice && matchesValue;
  });

  return (
    <div className="h-full flex flex-col" data-testid="gstr1-page">
      {/* Premium Header */}
      <ListPageHeader
        title="GSTR-1 – Outward Supplies"
        stats={[
          { icon: DollarSign, label: 'Total Value', value: formatCompactCurrency(summary?.total_taxable_value), color: 'emerald' },
          { icon: FileText, label: 'B2B Invoices', value: summary?.b2b_count || 0, color: 'blue' },
          { icon: TrendingUp, label: 'B2C Supplies', value: summary?.b2c_count || 0, color: 'purple' },
          { icon: Package, label: 'HSN Items', value: summary?.hsn_count || 0, color: 'amber' },
        ]}
        secondaryAction={{
          label: monthLabel,
          icon: Calendar,
          onClick: () => {}
        }}
      />

      {/* Controls Bar */}
      <div className="flex-shrink-0 px-2 pt-2">
        <div className="bg-white rounded-md border border-slate-200/60 shadow-sm p-3">
          <div className="flex items-center justify-between">
            {/* Back + Period Selector */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/business/${businessId}/reports`)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors text-[12px] font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              
              <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-1.5">
                <Calendar className="w-4 h-4 text-slate-400" />
                <select 
                  value={month} 
                  onChange={(e) => setMonth(parseInt(e.target.value))} 
                  className="text-[12px] text-slate-600 bg-transparent border-none focus:outline-none cursor-pointer"
                >
                  {months.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                <select 
                  value={year} 
                  onChange={(e) => setYear(parseInt(e.target.value))} 
                  className="text-[12px] text-slate-600 bg-transparent border-none focus:outline-none cursor-pointer"
                >
                  {years.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              {/* Section Tabs */}
              <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
                {sections.map(section => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium transition-all ${
                        activeSection === section.id 
                          ? 'bg-teal-600 text-white shadow-sm' 
                          : 'text-slate-600 hover:text-slate-800 hover:bg-white'
                      }`}
                    >
                      <Icon className={`w-3.5 h-3.5 ${activeSection === section.id ? 'text-white' : 'text-slate-400'}`} />
                      {section.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Export Buttons */}
            <div className="flex items-center gap-2">
              <button 
                onClick={() => handleExport('excel')}
                disabled={exporting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
              >
                <FileSpreadsheet className="w-3.5 h-3.5" />
                Excel
              </button>
              <button 
                onClick={() => handleExport('pdf')}
                disabled={exporting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-rose-600 rounded-lg hover:bg-rose-700 transition-colors disabled:opacity-50"
              >
                <FileText className="w-3.5 h-3.5" />
                PDF
              </button>
              <button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                <Download className="w-3.5 h-3.5" />
                JSON
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        {loading ? (
          <div className="flex-1 bg-white rounded-md border border-slate-200/60 shadow-sm flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
              <p className="text-[13px] text-slate-500 font-normal">Loading GSTR-1 data...</p>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
            {/* B2B Invoices Section */}
            {activeSection === 'b2b' && (
              <>
                {filteredB2B.length === 0 ? (
                  <div className="flex-1 flex flex-col items-center justify-center">
                    <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                      <Users className="w-7 h-7 text-slate-400" />
                    </div>
                    <p className="text-[15px] font-medium text-slate-600">No B2B invoices for this period</p>
                    <p className="text-[13px] text-slate-400 mt-1">B2B supplies to registered dealers will appear here</p>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col overflow-hidden">
                    {/* Table Header */}
                    <div className="flex-shrink-0">
                      <table className="tl-table">
                        <thead>
                          <tr>
                            <th className="tl-th tl-th-teal" style={{ width: '18%' }}>
                              <div className="flex items-center gap-1">
                                Customer GSTIN
                                <ColumnFilter 
                                  type="text" 
                                  value={columnFilters.gstin}
                                  onChange={(val) => setColumnFilters({...columnFilters, gstin: val})}
                                  placeholder="Filter GSTIN..."
                                />
                              </div>
                            </th>
                            <th className="tl-th tl-th-teal" style={{ width: '15%' }}>
                              <div className="flex items-center gap-1">
                                Invoice No
                                <ColumnFilter 
                                  type="text" 
                                  value={columnFilters.invoice_number}
                                  onChange={(val) => setColumnFilters({...columnFilters, invoice_number: val})}
                                  placeholder="Filter invoice..."
                                />
                              </div>
                            </th>
                            <th className="tl-th tl-th-teal" style={{ width: '12%' }}>Invoice Date</th>
                            <th className="tl-th tl-th-teal text-right" style={{ width: '14%' }}>
                              <div className="flex items-center justify-end gap-1">
                                Invoice Value
                                <ColumnFilter 
                                  type="number" 
                                  value={columnFilters.invoice_value}
                                  onChange={(val) => setColumnFilters({...columnFilters, invoice_value: val})}
                                />
                              </div>
                            </th>
                            <th className="tl-th tl-th-teal text-right" style={{ width: '12%' }}>Taxable Value</th>
                            <th className="tl-th tl-th-teal text-right" style={{ width: '10%' }}>IGST</th>
                            <th className="tl-th tl-th-teal text-right" style={{ width: '10%' }}>CGST</th>
                            <th className="tl-th tl-th-teal text-right" style={{ width: '9%' }}>SGST</th>
                          </tr>
                        </thead>
                      </table>
                    </div>

                    {/* Table Body */}
                    <div className="flex-1 overflow-y-auto">
                      <table className="tl-table">
                        <tbody className="animate-stagger">
                          {filteredB2B.map((invoice, idx) => (
                            <tr 
                              key={idx} 
                              className={`tl-tr hover:bg-teal-50/40 animate-tableRowFadeIn ${idx % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                              style={{ animationDelay: `${Math.min(idx * 30, 300)}ms` }}
                            >
                              <td className="tl-td tl-cell-primary" style={{ width: '18%' }}>{invoice.customer_gstin}</td>
                              <td className="tl-td tl-cell-regular" style={{ width: '15%' }}>{invoice.invoice_number}</td>
                              <td className="tl-td tl-cell-regular" style={{ width: '12%' }}>
                                {new Date(invoice.invoice_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </td>
                              <td className="tl-td tl-td-right text-[13px] font-semibold text-slate-800" style={{ width: '14%' }}>
                                ₹{invoice.invoice_value?.toLocaleString() || 0}
                              </td>
                              <td className="tl-td tl-td-right tl-cell-regular" style={{ width: '12%' }}>
                                ₹{invoice.taxable_value?.toLocaleString() || 0}
                              </td>
                              <td className="tl-td tl-td-right tl-cell-regular" style={{ width: '10%' }}>
                                ₹{invoice.igst?.toLocaleString() || 0}
                              </td>
                              <td className="tl-td tl-td-right tl-cell-regular" style={{ width: '10%' }}>
                                ₹{invoice.cgst?.toLocaleString() || 0}
                              </td>
                              <td className="tl-td tl-td-right tl-cell-regular" style={{ width: '9%' }}>
                                ₹{invoice.sgst?.toLocaleString() || 0}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Footer */}
                    <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                      <p>
                        Showing <span className="font-medium text-slate-700">{filteredB2B.length}</span> B2B invoices
                      </p>
                      <p>
                        Total Value: <span className="font-medium text-slate-700">₹{filteredB2B.reduce((sum, inv) => sum + (inv.invoice_value || 0), 0).toLocaleString()}</span>
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* B2C Small Section */}
            {activeSection === 'b2c' && (
              <>
                {(b2c_small || []).length === 0 ? (
                  <div className="flex-1 flex flex-col items-center justify-center">
                    <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                      <TrendingUp className="w-7 h-7 text-slate-400" />
                    </div>
                    <p className="text-[15px] font-medium text-slate-600">No B2C (Small) supplies for this period</p>
                    <p className="text-[13px] text-slate-400 mt-1">Inter-state supplies below ₹2.5L will appear here</p>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col overflow-hidden">
                    <div className="flex-shrink-0">
                      <table className="tl-table">
                        <thead>
                          <tr>
                            <th className="tl-th tl-th-teal" style={{ width: '25%' }}>Place of Supply</th>
                            <th className="tl-th tl-th-teal text-right" style={{ width: '20%' }}>Taxable Value</th>
                            <th className="tl-th tl-th-teal text-right" style={{ width: '15%' }}>IGST</th>
                            <th className="tl-th tl-th-teal text-right" style={{ width: '15%' }}>CGST</th>
                            <th className="tl-th tl-th-teal text-right" style={{ width: '15%' }}>SGST</th>
                            <th className="tl-th tl-th-teal text-right" style={{ width: '10%' }}>Cess</th>
                          </tr>
                        </thead>
                      </table>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                      <table className="tl-table">
                        <tbody>
                          {(b2c_small || []).map((item, idx) => (
                            <tr key={idx} className={`tl-tr hover:bg-teal-50/40 ${idx % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}>
                              <td className="tl-td tl-cell-primary" style={{ width: '25%' }}>{item.place_of_supply}</td>
                              <td className="tl-td tl-td-right text-[13px] font-semibold text-slate-800" style={{ width: '20%' }}>
                                ₹{item.taxable_value?.toLocaleString() || 0}
                              </td>
                              <td className="tl-td tl-td-right tl-cell-regular" style={{ width: '15%' }}>₹{item.igst?.toLocaleString() || 0}</td>
                              <td className="tl-td tl-td-right tl-cell-regular" style={{ width: '15%' }}>₹{item.cgst?.toLocaleString() || 0}</td>
                              <td className="tl-td tl-td-right tl-cell-regular" style={{ width: '15%' }}>₹{item.sgst?.toLocaleString() || 0}</td>
                              <td className="tl-td tl-td-right tl-cell-regular" style={{ width: '10%' }}>₹{item.cess?.toLocaleString() || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* HSN Summary Section */}
            {activeSection === 'hsn' && (
              <>
                {(hsn_summary || []).length === 0 ? (
                  <div className="flex-1 flex flex-col items-center justify-center">
                    <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                      <Package className="w-7 h-7 text-slate-400" />
                    </div>
                    <p className="text-[15px] font-medium text-slate-600">No HSN data for this period</p>
                    <p className="text-[13px] text-slate-400 mt-1">HSN-wise summary of supplies will appear here</p>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col overflow-hidden">
                    <div className="flex-shrink-0">
                      <table className="tl-table">
                        <thead>
                          <tr>
                            <th className="tl-th tl-th-teal" style={{ width: '12%' }}>HSN Code</th>
                            <th className="tl-th tl-th-teal" style={{ width: '25%' }}>Description</th>
                            <th className="tl-th tl-th-teal" style={{ width: '8%' }}>UQC</th>
                            <th className="tl-th tl-th-teal text-right" style={{ width: '10%' }}>Qty</th>
                            <th className="tl-th tl-th-teal text-right" style={{ width: '15%' }}>Total Value</th>
                            <th className="tl-th tl-th-teal text-right" style={{ width: '10%' }}>IGST</th>
                            <th className="tl-th tl-th-teal text-right" style={{ width: '10%' }}>CGST</th>
                            <th className="tl-th tl-th-teal text-right" style={{ width: '10%' }}>SGST</th>
                          </tr>
                        </thead>
                      </table>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                      <table className="tl-table">
                        <tbody>
                          {(hsn_summary || []).map((item, idx) => (
                            <tr key={idx} className={`tl-tr hover:bg-teal-50/40 ${idx % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}>
                              <td className="tl-td">
                                <span className="inline-flex px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-[11px] font-mono font-medium">
                                  {item.hsn_code}
                                </span>
                              </td>
                              <td className="tl-td tl-cell-primary">{item.description}</td>
                              <td className="tl-td tl-cell-regular">{item.uqc || 'NOS'}</td>
                              <td className="tl-td tl-td-right tl-cell-regular">{item.total_quantity?.toLocaleString() || 0}</td>
                              <td className="tl-td tl-td-right text-[13px] font-semibold text-slate-800">₹{item.total_value?.toLocaleString() || 0}</td>
                              <td className="tl-td tl-td-right tl-cell-regular">₹{item.igst?.toLocaleString() || 0}</td>
                              <td className="tl-td tl-td-right tl-cell-regular">₹{item.cgst?.toLocaleString() || 0}</td>
                              <td className="tl-td tl-td-right tl-cell-regular">₹{item.sgst?.toLocaleString() || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GSTR1Page;
